import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Package, Percent, IndianRupee, CalendarRange, ShieldCheck, Save, AlertTriangle, RefreshCw } from "lucide-react";
import { message } from "antd";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { AccessDenied } from "../context/Permissionguards";
import { fintechApi } from "../services/api";

// Backend LoanProduct entity ke loanType values
const LOAN_TYPES = ["PERSONAL", "BUSINESS", "HOME", "VEHICLE", "EDUCATION", "GOLD", "BNPL", "MSME"];

const EMPTY_FORM = {
  productName: "",
  loanType: "PERSONAL",
  interestRate: "",
  processingFeePercent: "",
  minAmount: "",
  maxAmount: "",
  minTenureMonths: "",
  maxTenureMonths: "",
  moratoriumMonths: "0",
  prepaymentAllowed: true,
  prepaymentPenaltyPercent: "",
  partPaymentAllowed: true,
  lateFeePercent: "",
  lateFeeFixed: "",
  isActive: true,
};

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
};

// Inputs string me rakhte hain (khaali allowed), submit par number banate hain
const str = (v) => (v === undefined || v === null ? "" : String(v));

const productToForm = (p) => ({
  productName: str(p?.productName ?? p?.name),
  loanType: p?.loanType ?? p?.type ?? "PERSONAL",
  interestRate: str(p?.interestRate),
  processingFeePercent: str(p?.processingFeePercent),
  minAmount: str(p?.minAmount ?? p?.minLoanAmount),
  maxAmount: str(p?.maxAmount ?? p?.maxLoanAmount),
  minTenureMonths: str(p?.minTenureMonths ?? p?.minTenure),
  maxTenureMonths: str(p?.maxTenureMonths ?? p?.maxTenure),
  moratoriumMonths: str(p?.moratoriumMonths ?? 0),
  prepaymentAllowed: p?.prepaymentAllowed ?? true,
  prepaymentPenaltyPercent: str(p?.prepaymentPenaltyPercent),
  partPaymentAllowed: p?.partPaymentAllowed ?? true,
  lateFeePercent: str(p?.lateFeePercent),
  lateFeeFixed: str(p?.lateFeeFixed),
  isActive: p?.isActive ?? p?.active ?? true,
});

const toArray = (res) =>
  Array.isArray(res) ? res : Array.isArray(res?.content) ? res.content : Array.isArray(res?.data) ? res.data : [];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const inputCls =
  "w-full px-3.5 py-2.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]";

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-bold text-gray-700 mb-1">
      {label} {required && "*"}
    </label>
    {children}
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <span
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-[#2e856e]" : "bg-gray-300"}`}
      role="switch"
      aria-checked={checked}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </span>
    <span className="text-sm font-semibold text-gray-800">{label}</span>
  </label>
);

export default function LoanProductForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { currentTheme } = useTheme();
  const { isAdmin, has } = usePermissions();
  const primaryColor = currentTheme?.color || "#2e856e";
  const canManage = isAdmin || has("LOAN_PRODUCT_MANAGE");

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Edit: list page product state me bhejta hai; refresh par list se dhoondho
  // (backend par GET /loan-products/{id} nahi hai, sirf list hai)
  useEffect(() => {
    if (!isEdit) return;
    const fromState = location.state?.product;
    if (fromState && String(fromState.id ?? fromState.productId) === String(id)) {
      setForm(productToForm(fromState));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fintechApi.getLoanProducts();
        const found = toArray(res).find((p) => String(p?.id ?? p?.productId) === String(id));
        if (cancelled) return;
        if (!found) {
          setLoadError(`Loan product #${id} not found`);
        } else {
          setForm(productToForm(found));
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || "Failed to load loan product");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, location.state]);

  // Preview: max amount (ya 1 lakh) par max tenure ke liye EMI
  const preview = useMemo(() => {
    const P = num(form.maxAmount) || num(form.minAmount) || 100000;
    const N = num(form.maxTenureMonths) || num(form.minTenureMonths) || 12;
    const rate = num(form.interestRate);
    if (!rate || rate <= 0 || P <= 0 || N <= 0) return null;
    const R = rate / 12 / 100;
    const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
    const fee = (P * (num(form.processingFeePercent) || 0)) / 100;
    return { P, N, emi: Math.round(emi), fee: Math.round(fee) };
  }, [form.maxAmount, form.minAmount, form.maxTenureMonths, form.minTenureMonths, form.interestRate, form.processingFeePercent]);

  const validate = () => {
    if (!form.productName.trim()) return "Product name is required";
    if (!LOAN_TYPES.includes(form.loanType)) return "Select a loan type";
    if (!(num(form.interestRate) > 0)) return "Interest rate must be greater than 0";
    const minA = num(form.minAmount), maxA = num(form.maxAmount);
    if (minA !== null && maxA !== null && minA > maxA) return "Min amount cannot exceed max amount";
    const minT = num(form.minTenureMonths), maxT = num(form.maxTenureMonths);
    if (minT !== null && maxT !== null && minT > maxT) return "Min tenure cannot exceed max tenure";
    for (const [k, label] of [["processingFeePercent", "Processing fee"], ["prepaymentPenaltyPercent", "Prepayment penalty"], ["lateFeePercent", "Late fee %"], ["lateFeeFixed", "Late fee amount"], ["moratoriumMonths", "Moratorium"]]) {
      const v = num(form[k]);
      if (v !== null && v < 0) return `${label} cannot be negative`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      message.warning(err);
      return;
    }
    const payload = {
      productName: form.productName.trim(),
      loanType: form.loanType,
      interestRate: num(form.interestRate),
      processingFeePercent: num(form.processingFeePercent),
      minAmount: num(form.minAmount),
      maxAmount: num(form.maxAmount),
      minTenureMonths: num(form.minTenureMonths),
      maxTenureMonths: num(form.maxTenureMonths),
      moratoriumMonths: num(form.moratoriumMonths) ?? 0,
      prepaymentAllowed: !!form.prepaymentAllowed,
      prepaymentPenaltyPercent: form.prepaymentAllowed ? num(form.prepaymentPenaltyPercent) : null,
      partPaymentAllowed: !!form.partPaymentAllowed,
      lateFeePercent: num(form.lateFeePercent),
      lateFeeFixed: num(form.lateFeeFixed),
      isActive: !!form.isActive,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await fintechApi.updateLoanProduct(id, payload);
        message.success("Loan product updated");
      } else {
        await fintechApi.createLoanProduct(payload);
        message.success("Loan product created");
      }
      navigate("/fintech/loan-products");
    } catch (err) {
      console.error("Loan product save failed:", err);
      message.error(err?.message || "Failed to save loan product");
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) return <AccessDenied detail="Required: LOAN_PRODUCT_MANAGE" />;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-white p-10 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center text-center gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <h2 className="text-lg font-bold text-gray-900">Could not load loan product</h2>
        <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
        <div className="flex gap-2 mt-2">
          <button onClick={() => navigate("/fintech/loan-products")} className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50">
            Back to Products
          </button>
          <button onClick={() => navigate(0)} className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6 pb-16">
      {/* ── Page Header ── */}
      <div className="flex items-center justify-between bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate("/fintech/loan-products")} className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md" style={{ backgroundColor: primaryColor }}>
            <Package className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">{isEdit ? "Edit Loan Product" : "New Loan Product"}</h1>
            <p className="text-xs md:text-sm text-gray-500">{isEdit ? `Product #${id}` : "Define a loan scheme offered to applicants"}</p>
          </div>
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${form.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
          {form.isActive ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* ── Basic ── */}
          <section className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-4">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Package className="w-5 h-5" style={{ color: primaryColor }} /> Basic Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Product Name" required>
                  <input type="text" value={form.productName} onChange={(e) => set("productName", e.target.value)} placeholder="e.g. Personal Loan - Salaried" className={inputCls} maxLength={100} />
                </Field>
              </div>
              <Field label="Loan Type" required>
                <select value={form.loanType} onChange={(e) => set("loanType", e.target.value)} className={inputCls}>
                  {LOAN_TYPES.map((t) => (
                    <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>
                  ))}
                </select>
              </Field>
              <div className="flex items-end pb-1">
                <Toggle label="Active (offered for new applications)" checked={!!form.isActive} onChange={(v) => set("isActive", v)} />
              </div>
            </div>
          </section>

          {/* ── Pricing & Limits ── */}
          <section className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-4">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <Percent className="w-5 h-5" style={{ color: primaryColor }} /> Pricing &amp; Limits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Interest Rate (% p.a.)" required>
                <input type="number" min={0} max={60} step={0.25} value={form.interestRate} onChange={(e) => set("interestRate", e.target.value)} placeholder="e.g. 12.5" className={inputCls} />
              </Field>
              <Field label="Processing Fee (%)" hint="Percentage of the loan amount, deducted at disbursal">
                <input type="number" min={0} max={20} step={0.1} value={form.processingFeePercent} onChange={(e) => set("processingFeePercent", e.target.value)} placeholder="e.g. 1.5" className={inputCls} />
              </Field>
              <Field label="Min Loan Amount (₹)">
                <input type="number" min={0} step={1000} value={form.minAmount} onChange={(e) => set("minAmount", e.target.value)} placeholder="e.g. 25000" className={inputCls} />
              </Field>
              <Field label="Max Loan Amount (₹)">
                <input type="number" min={0} step={1000} value={form.maxAmount} onChange={(e) => set("maxAmount", e.target.value)} placeholder="e.g. 1000000" className={inputCls} />
              </Field>
              <Field label="Min Tenure (months)">
                <input type="number" min={1} max={360} value={form.minTenureMonths} onChange={(e) => set("minTenureMonths", e.target.value)} placeholder="e.g. 6" className={inputCls} />
              </Field>
              <Field label="Max Tenure (months)">
                <input type="number" min={1} max={360} value={form.maxTenureMonths} onChange={(e) => set("maxTenureMonths", e.target.value)} placeholder="e.g. 60" className={inputCls} />
              </Field>
              <Field label="Moratorium (months)" hint="Months after disbursal before the first EMI is due">
                <input type="number" min={0} max={24} value={form.moratoriumMonths} onChange={(e) => set("moratoriumMonths", e.target.value)} className={inputCls} />
              </Field>
            </div>
          </section>

          {/* ── Repayment Rules ── */}
          <section className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-4">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3">
              <ShieldCheck className="w-5 h-5" style={{ color: primaryColor }} /> Repayment Rules
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Toggle label="Prepayment / foreclosure allowed" checked={!!form.prepaymentAllowed} onChange={(v) => set("prepaymentAllowed", v)} />
                <Field label="Prepayment Penalty (%)" hint={form.prepaymentAllowed ? "Charged on the outstanding principal" : "Prepayment is disabled"}>
                  <input type="number" min={0} max={20} step={0.1} disabled={!form.prepaymentAllowed} value={form.prepaymentPenaltyPercent} onChange={(e) => set("prepaymentPenaltyPercent", e.target.value)} placeholder="e.g. 2" className={`${inputCls} disabled:opacity-50`} />
                </Field>
              </div>
              <div className="space-y-3">
                <Toggle label="Part payment allowed" checked={!!form.partPaymentAllowed} onChange={(v) => set("partPaymentAllowed", v)} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Late Fee (%)">
                    <input type="number" min={0} max={20} step={0.1} value={form.lateFeePercent} onChange={(e) => set("lateFeePercent", e.target.value)} placeholder="e.g. 2" className={inputCls} />
                  </Field>
                  <Field label="Late Fee Fixed (₹)">
                    <input type="number" min={0} value={form.lateFeeFixed} onChange={(e) => set("lateFeeFixed", e.target.value)} placeholder="e.g. 500" className={inputCls} />
                  </Field>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ── Preview + Actions ── */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-gradient-to-br from-[#2e856e]/10 via-emerald-50/50 to-white p-6 rounded-2xl border border-emerald-200/80 shadow-sm">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: primaryColor }}>Example EMI</span>
            {preview ? (
              <>
                <div className="text-3xl font-black text-gray-900 mt-2">
                  {formatINR(preview.emi)} <span className="text-xs font-medium text-gray-500">/ month</span>
                </div>
                <div className="space-y-2 my-4 text-xs border-y border-emerald-200/60 py-4 text-gray-600">
                  <div className="flex justify-between"><span className="flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5" /> Amount</span><strong className="text-gray-900">{formatINR(preview.P)}</strong></div>
                  <div className="flex justify-between"><span className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5" /> Tenure</span><strong className="text-gray-900">{preview.N} months</strong></div>
                  <div className="flex justify-between"><span className="flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" /> Rate</span><strong className="text-gray-900">{form.interestRate}% p.a.</strong></div>
                  <div className="flex justify-between"><span>Processing fee</span><strong className="text-gray-900">{formatINR(preview.fee)}</strong></div>
                </div>
                <p className="text-[11px] text-gray-500">Based on max amount and max tenure, reducing balance method.</p>
              </>
            ) : (
              <p className="text-sm text-gray-500 mt-2">Enter an interest rate to see an example EMI.</p>
            )}
          </div>

          <div className="bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col gap-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all hover:brightness-105 active:scale-95 disabled:opacity-60"
              style={{ backgroundColor: primaryColor }}
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Product"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/fintech/loan-products")}
              disabled={saving}
              className="w-full px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
